import { AudioMessageKeyEnum, Button } from '@cibli/shared';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import VocalBar from '@/components/organisms/VocalBar.tsx';
import IconButton from '@/features/common/components/IconButton';
import MainLayout from '@/features/common/components/MainLayout';
import { useAudioMessage } from '@/hooks/useAudioMessage.ts';
import { getCountryCode } from '@/utils/getCountryCode.ts';

import DotNavigation from '../components/DotNavigation';
import { OnboardingStep } from '../components/OnboardingStep';

interface Props {
  onStart: () => void;
  onCancel: () => void;
  onBack?: () => void;
}

export default function OnboardingScreen({ onStart, onCancel, onBack }: Props) {
  const { t, i18n } = useTranslation();
  const steps = useMemo(
    () => [
      {
        image: '/microphone-256.png',
        title: t('OnboardingScreen.step_one.title'),
        description: t('OnboardingScreen.step_one.description'),
      },
      {
        image: '/board-256.png',
        title: t('OnboardingScreen.step_two.title'),
        description: t('OnboardingScreen.step_two.description'),
      },
      {
        image: '/dart-256.png',
        title: t('OnboardingScreen.step_three.title'),
        description: t('OnboardingScreen.step_three.description'),
      },
    ],
    [t],
  );
  const [currentStep, setCurrentStep] = useState(0);
  const [direction, setDirection] = useState(0);

  const { isAudioLoading, isPlaying } = useAudioMessage(AudioMessageKeyEnum.WELCOME);

  const handleStepChange = (newStep: number) => {
    setDirection(newStep > currentStep ? 1 : -1);
    setCurrentStep(newStep);
  };

  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? '30%' : '-30%',
      opacity: 0,
      height: 'auto',
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1,
      height: 'auto',
    },
    exit: (direction: number) => ({
      zIndex: 0,
      x: direction < 0 ? '30%' : '-30%',
      opacity: 0,
      height: 'auto',
    }),
  };

  return (
    <>
      <MainLayout
        className="gap-10 pb-24"
        onClose={onCancel}
        rightContent={
          onBack ? (
            <IconButton
              className="ml-6"
              icon={<ArrowLeft strokeWidth={3} className="min-h-6 min-w-6" />}
              onClick={onBack}
            />
          ) : undefined
        }
      >
        <AnimatePresence initial={false} mode="wait" custom={direction}>
          <motion.div
            key={currentStep}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{
              x: { type: 'tween', duration: 0.2, ease: 'easeInOut' },
              opacity: { duration: 0.2 },
            }}
          >
            <OnboardingStep
              image={steps[currentStep].image}
              title={steps[currentStep].title}
              description={steps[currentStep].description}
            />
          </motion.div>
        </AnimatePresence>

        <DotNavigation
          currentStep={currentStep}
          totalSteps={steps.length}
          onStepChange={handleStepChange}
          className="w-full max-w-xl"
        />
        <Button
          className="w-full sm:w-auto"
          size="xl"
          autoFocus
          onClick={() => {
            onStart();
          }}
        >
          {t('common.start')}
        </Button>

        <VocalBar
          message={isAudioLoading ? t('WelcomeScreen.loading') : t('WelcomeScreen.title')}
          countryCode={getCountryCode(i18n.language)}
          showFlag={false}
          showControls={false}
          statusCircleProps={{
            size: 40,
            isLoading: isAudioLoading,
            isPlaying,
            centered: true,
          }}
        />
      </MainLayout>
    </>
  );
}
